import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const STORAGE_KEY = "ithaca_nl_seen";
const DELAY_MS = 120000; // 2 minutes
const RE_ARM_MS = 3600 * 1000; // if flag is older than 1h, treat as unseen if user cleared it — not used, kept simple

export default function NewsletterPopup() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done | error
  const [err, setErr] = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    // Only mount timer once per browser session per visitor.
    let seen = null;
    try { seen = window.localStorage.getItem(STORAGE_KEY); } catch { /* SSR / privacy mode */ }
    if (seen) return;
    timerRef.current = window.setTimeout(() => setOpen(true), DELAY_MS);
    return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
  }, []);

  function dismiss() {
    setOpen(false);
    try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    const val = email.trim().toLowerCase();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setErr(t("newsletter.error"));
      setState("error");
      return;
    }
    setState("submitting");
    try {
      await api.post("/newsletter/subscribe", {
        email: val,
        language: lang || "en",
        source: "home_popup",
      });
      try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
      setState("done");
      setEmail("");
      window.setTimeout(() => setOpen(false), 3200);
    } catch (e2) {
      setErr(e2.response?.data?.detail || t("newsletter.error"));
      setState("error");
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="nlpop"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          data-testid="newsletter-popup"
          className="fixed bottom-6 right-6 left-6 md:left-auto md:w-[420px] z-[80] pointer-events-auto"
        >
          <div className="bg-stone_white border border-soft_border shadow-[0_10px_40px_-10px_rgba(10,25,47,0.25)]">
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close"
              data-testid="newsletter-popup-close"
              className="absolute top-3 right-3 text-ionian/40 hover:text-ionian transition text-xl leading-none w-8 h-8 flex items-center justify-center"
            >
              ×
            </button>

            <div className="p-6 md:p-8">
              {state === "done" ? (
                <div data-testid="newsletter-popup-success">
                  <div className="overline text-brass">✓</div>
                  <div className="font-serif text-2xl mt-2 leading-tight">{t("newsletter.success")}</div>
                  <div className="text-xs text-ink/55 italic mt-3 leading-relaxed">
                    A first letter is on its way to your inbox.
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="newsletter-popup-form">
                  <div className="overline">{t("newsletter.overline")}</div>
                  <div className="font-serif text-2xl md:text-3xl mt-2 leading-[1.1]">
                    {t("newsletter.heading")}
                  </div>
                  <p className="text-sm text-ink/70 mt-3 leading-relaxed">
                    {t("newsletter.body")}
                  </p>

                  <div className="mt-5 flex gap-2">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder={t("newsletter.email_placeholder")}
                      className="editorial-input flex-1"
                      autoComplete="email"
                      data-testid="newsletter-popup-email"
                    />
                    <button
                      type="submit"
                      disabled={state === "submitting"}
                      className="px-5 py-2 bg-ionian text-stone_white text-xs uppercase tracking-widest hover:bg-ionian/90 transition disabled:opacity-60"
                      data-testid="newsletter-popup-submit"
                    >
                      {state === "submitting" ? "…" : t("newsletter.submit")}
                    </button>
                  </div>
                  {err && (
                    <div data-testid="newsletter-popup-error" className="text-terracotta text-xs mt-2">{err}</div>
                  )}
                  <div className="text-[11px] text-ink/50 italic mt-4 leading-relaxed">
                    {t("newsletter.consent")}
                  </div>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
